import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faGear, faCamera, faPenToSquare, 
  faCalendarDays, faVenusMars, faPlus, faTrash,
  faHome, faBell, faUser, faSpinner, faCheck, faXmark
} from '@fortawesome/free-solid-svg-icons';
import { userService, minorService } from '../services/database';
import { User, Minor } from '../lib/supabase';
import { getCurrentUser } from '../utils/auth';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [minors, setMinors] = useState<Minor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      
      // Get the currently logged-in user
      const currentUser = getCurrentUser();
      if (!currentUser) {
        console.log('No user logged in, redirecting to login');
        navigate('/login');
        return;
      }
      
      // Load full user data from database using the logged-in user's email
      const userData = await userService.getUserByEmail(currentUser.email);
      if (!userData) {
        setError('User profile not found in database.');
        return;
      }
      
      setUser(userData);
      
      // Load user's minors using the actual user ID
      const minorsData = await minorService.getUserMinors(userData.id);
      setMinors(minorsData);
      
      console.log('Loaded profile data for user:', {
        name: userData.name,
        email: userData.email,
        id: userData.id,
        minorsCount: minorsData.length
      });
      
      setError(null);
    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Failed to load profile data.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const startEditing = (field: string, currentValue: string) => {
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
  };

  const saveField = async () => {
    if (!user || !editingField) return;

    try {
      setSaving(true);
      
      const updates: Partial<User> = {
        [editingField]: editValue
      };

      // Update user in database
      const updatedUser = await userService.updateUser(user.id, updates);
      
      // Update local state
      setUser(updatedUser);
      
      // Reset editing state
      setEditingField(null);
      setEditValue('');
      
      console.log(`Updated ${editingField} to:`, editValue);
      
    } catch (err) {
      console.error('Error updating user field:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-4">
        <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary animate-spin mb-4" />
        <p className="text-gray-600">Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-4">
        <h2 className="text-xl font-semibold mb-4">Error Loading Profile</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <button 
          onClick={loadUserData}
          className="bg-primary text-white px-6 py-2 rounded-lg"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-4">
        <h2 className="text-xl font-semibold mb-4">No Profile Found</h2>
        <p className="text-gray-600 mb-6">Please create a profile to continue.</p>
        <Link to="/register" className="bg-primary text-white px-6 py-2 rounded-lg">
          Create Profile
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 font-sans text-dark">
      {/* Mobile Header */}
      <header className="app-header">
        <div className="flex items-center justify-between">
          <Link to="/" className="app-header-back">
            <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
          </Link>
          <h1 className="app-header-title">Profile</h1>
          <div className="w-6"></div> {/* Empty div for spacing */}
        </div>
      </header>

      {/* Main Content */}
      <main className="content-container pb-20">
        {/* Profile Section */}
        <section className="bg-white p-4 mb-3">
          <div className="flex items-center">
            <div className="relative">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200 border-2 border-primary">
                {user.profile_image_url ? (
                  <img 
                    src={user.profile_image_url} 
                    alt="Profile" 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full bg-primary flex items-center justify-center text-white text-2xl font-bold">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <button className="absolute bottom-0 right-0 bg-primary text-white rounded-full p-1 shadow">
                <FontAwesomeIcon icon={faCamera} className="text-xs" />
              </button>
            </div>
            <div className="ml-4">
              <h2 className="text-xl font-bold">{user.name}</h2>
              <p className="text-gray-600 text-sm">{user.rider_category || 'Rider'}</p>
              <div className="flex mt-2">
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">Verified</span>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Actions Section */}
        <section className="bg-white p-4 mb-3">
          <div className="flex space-x-3">
            <Link 
              to="/dashboard" 
              className="flex-1 bg-primary text-white px-4 py-3 rounded-lg flex items-center justify-center space-x-2 hover:bg-orange-600 transition-colors"
            >
              <FontAwesomeIcon icon={faHome} className="text-lg" />
              <span className="font-medium">Go to Dashboard</span>
            </Link>
          </div>
        </section>

        {/* Personal Info Section */}
        <section className="bg-white mb-3">
          <div className="px-4 py-3 border-b">
            <h3 className="font-semibold text-gray-800">Personal Information</h3>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex-1">
                <p className="text-gray-500 text-sm">Email</p>
                {editingField === 'email' ? (
                  <div className="flex items-center space-x-2 mt-1">
                    <input
                      type="email"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                      autoFocus
                    />
                    <button
                      onClick={saveField}
                      disabled={saving}
                      className="text-green-600 hover:text-green-700 disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                    <button
                      onClick={cancelEditing}
                      disabled={saving}
                      className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-800">{user.email}</p>
                )}
              </div>
              {editingField !== 'email' && (
                <button 
                  className="text-primary hover:text-orange-600"
                  onClick={() => startEditing('email', user.email)}
                >
                  <FontAwesomeIcon icon={faPenToSquare} />
                </button>
              )}
            </div>
            <div className="flex justify-between items-center">
              <div className="flex-1">
                <p className="text-gray-500 text-sm">Phone</p>
                {editingField === 'phone' ? (
                  <div className="flex items-center space-x-2 mt-1">
                    <input
                      type="tel"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                      autoFocus
                    />
                    <button
                      onClick={saveField}
                      disabled={saving}
                      className="text-green-600 hover:text-green-700 disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                    <button
                      onClick={cancelEditing}
                      disabled={saving}
                      className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-800">{user.phone}</p>
                )}
              </div>
              {editingField !== 'phone' && (
                <button 
                  className="text-primary hover:text-orange-600"
                  onClick={() => startEditing('phone', user.phone)}
                >
                  <FontAwesomeIcon icon={faPenToSquare} />
                </button>
              )}
            </div>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-500 text-sm">Date of Birth</p>
                <p className="text-gray-800">{user.date_of_birth ? formatDate(user.date_of_birth) : 'Not provided'}</p>
              </div>
              <button className="text-primary">
                <FontAwesomeIcon icon={faPenToSquare} />
              </button>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-500 text-sm">Gender</p>
                <p className="text-gray-800">{user.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : 'Not specified'}</p>
              </div>
              <button className="text-primary">
                <FontAwesomeIcon icon={faPenToSquare} />
              </button>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-500 text-sm">Rider Category</p>
                <p className="text-gray-800">{user.rider_category || 'Not specified'}</p>
              </div>
              <button className="text-primary">
                <FontAwesomeIcon icon={faPenToSquare} />
              </button>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-500 text-sm">CSA Licence</p>
                <p className="text-gray-800">{user.csa_licence || 'Not provided'}</p>
              </div>
              <button className="text-primary">
                <FontAwesomeIcon icon={faPenToSquare} />
              </button>
            </div>
          </div>
        </section>

        {/* Emergency Contact Section */}
        <section className="bg-white mb-3">
          <div className="px-4 py-3 border-b">
            <h3 className="font-semibold text-gray-800">Emergency Contact</h3>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex-1">
                <p className="text-gray-500 text-sm">Name</p>
                {editingField === 'emergency_contact_name' ? (
                  <div className="flex items-center space-x-2 mt-1">
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder="Emergency contact name"
                      autoFocus
                    />
                    <button
                      onClick={saveField}
                      disabled={saving}
                      className="text-green-600 hover:text-green-700 disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                    <button
                      onClick={cancelEditing}
                      disabled={saving}
                      className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-800">{user.emergency_contact_name || 'Not provided'}</p>
                )}
              </div>
              {editingField !== 'emergency_contact_name' && (
                <button 
                  className="text-primary hover:text-orange-600"
                  onClick={() => startEditing('emergency_contact_name', user.emergency_contact_name || '')}
                >
                  <FontAwesomeIcon icon={faPenToSquare} />
                </button>
              )}
            </div>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-500 text-sm">Phone</p>
                <p className="text-gray-800">{user.emergency_contact_phone || 'Not provided'}</p>
              </div>
              <button className="text-primary">
                <FontAwesomeIcon icon={faPenToSquare} />
              </button>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-500 text-sm">Relationship</p>
                <p className="text-gray-800">{user.emergency_contact_relationship || 'Not specified'}</p>
              </div>
              <button className="text-primary">
                <FontAwesomeIcon icon={faPenToSquare} />
              </button>
            </div>
          </div>
        </section>

        {/* Medical Information Section */}
        <section className="bg-white mb-3">
          <div className="px-4 py-3 border-b">
            <h3 className="font-semibold text-gray-800">Medical Information</h3>
          </div>
          <div className="p-4">
            <div className="mb-4">
              <p className="text-gray-500 text-sm">Allergies</p>
              <p className="text-gray-800">{user.allergies || 'None reported'}</p>
            </div>
            <div className="mb-4">
              <p className="text-gray-500 text-sm">Medical Conditions</p>
              <p className="text-gray-800">{user.medical_conditions || 'None reported'}</p>
            </div>
            <button className="w-full py-2 bg-gray-100 text-primary rounded-md flex items-center justify-center">
              <FontAwesomeIcon icon={faPenToSquare} className="mr-2" />
              Edit Medical Information
            </button>
          </div>
        </section>

        {/* Registered Minors Section */}
        <section className="bg-white mb-3">
          <div className="px-4 py-3 border-b flex justify-between items-center">
            <h3 className="font-semibold text-gray-800">Registered Minors</h3>
            <Link to="/add-minor" className="text-primary text-sm font-medium flex items-center">
              <FontAwesomeIcon icon={faPlus} className="mr-1" /> Add
            </Link>
          </div>
          <div className="p-4">
            {minors.length > 0 ? (
              minors.map((minor) => (
                <div key={minor.id} className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-200">
                  <div className="flex justify-between">
                    <h4 className="font-medium">{minor.name}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      minor.rider_category === 'Beginner' ? 'bg-green-100 text-green-800' :
                      minor.rider_category === 'Intermediate' ? 'bg-orange-100 text-orange-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {minor.rider_category}
                    </span>
                  </div>
                  <div className="mt-2 flex text-sm text-gray-600">
                    <div className="mr-4">
                      <FontAwesomeIcon icon={faCalendarDays} className="mr-1 text-gray-400" />
                      {calculateAge(minor.date_of_birth)} years
                    </div>
                    <div>
                      <FontAwesomeIcon icon={faVenusMars} className="mr-1 text-gray-400" />
                      {minor.gender.charAt(0).toUpperCase() + minor.gender.slice(1)}
                    </div>
                  </div>
                  <div className="mt-2 flex justify-end space-x-2">
                    <button className="text-primary p-1">
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </button>
                    <button className="text-red-500 p-1">
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-gray-500">
                <p className="mb-2">No minor children registered</p>
                <Link to="/add-minor" className="text-primary text-sm">
                  Add your first minor
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Bottom Navigation */}
      <nav className="app-bottom-nav">
        <div className="flex justify-around">
          <Link to="/" className="app-bottom-nav-link py-2 px-3">
            <FontAwesomeIcon icon={faHome} className="text-lg" />
            <span className="text-xs mt-1">Home</span>
          </Link>
          <Link to="/events" className="app-bottom-nav-link py-2 px-3">
            <FontAwesomeIcon icon={faCalendarDays} className="text-lg" />
            <span className="text-xs mt-1">Events</span>
          </Link>
          <Link to="/profile" className="app-bottom-nav-link-active py-2 px-3">
            <FontAwesomeIcon icon={faUser} className="text-lg" />
            <span className="text-xs mt-1">Profile</span>
          </Link>
          <span className="app-bottom-nav-link py-2 px-3 cursor-pointer">
            <FontAwesomeIcon icon={faBell} className="text-lg" />
            <span className="text-xs mt-1">Alerts</span>
          </span>
        </div>
      </nav>
    </div>
  );
};

export default Profile;
